import { FC, JSX, useContext, useMemo } from "react";
import { isNullOrUndefined } from "@app/shared/utility/isNullOrUndefined";
import { CalculationMethodEnum } from "@app/templates/templateModels";
import { MapDetailsBoxProps } from "@app/projects/network-assessment/maps/network-coverage-map/interfaces";
import {
  NetworkCoverageMapDataContext
} from "@app/projects/network-assessment/maps/network-coverage-map/contexts/NetworkCoverageMapDataContext";
import {
  HUNDRED_PERCENT_FILTER,
  ZERO_PERCENT_FILTER
} from "@app/projects/network-assessment/maps/network-coverage-map/NetworkCoverageMapsConstants";
import {
  ConvertProviderSourceFilterOptionsToDisplay
} from "@app/projects/network-assessment/maps/network-coverage-map/DisplayConverters";
import { InfoBadge } from "@app/shared/InfoBadge";
import NetworkCoverageMapCountyDetailInfo
  from "@app/projects/network-assessment/maps/network-coverage-map/components/NetworkCoverageMapCountyDetailInfo";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { FilterContext } from "@app/projects/network-assessment/maps/network-coverage-map/contexts/FilterContext";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { QesColors } from "@app/shared/qes-standards/colors";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { getMembershipTypeLabel } from "@app/projects/membershipTypeLabel";
import { truncateStringWithEllipsis } from "@app/shared/utility/truncateStringWithEllipsis";
import TooltipWrapper from "@app/shared/TooltipWrapper";

const MAX_MEMBERSHIP_CHIP_LENGTH = 20;

const MapDetailsBox: FC<MapDetailsBoxProps> = ({ cmsCreditIsApplying, adequacySummarySpecialtyGroup, project, runConfiguration }) => {
  const mapDataContext = useContext(NetworkCoverageMapDataContext);
  const filterContext = useContext(FilterContext);
  const ffContext = useContext(FeatureFlagContext);

  const membershipLabel = ffContext.enabledFeatureFlags.has(Features.DUAL_MEMBERSHIP_FOR_MAPS)
    ? getMembershipTypeLabel(runConfiguration)
    : null;

  const adequacyTimeslices = filterContext.adequacyTimeslices;
  const pciDataEnabled = adequacyTimeslices?.find(prc => prc.projectRunConfigurationId == filterContext.selectedTimeslice)?.showPciData ?? false;

  const countyStatusColors = useMemo(() => {
    const countyStatusToColor: Record<RegionSetStatusType, {fillColor: string, backgroundColor: string}> = {
      [RegionSetStatusType.Active]: {
        backgroundColor: "#DCECFF",
        fillColor: "#056EDF",
      },
      [RegionSetStatusType.Pending]: {
        backgroundColor: "#FFF7DD",
        fillColor: "#FFD654",
      },
      [RegionSetStatusType.Approved]: {
        backgroundColor: "#CCF5E7",
        fillColor: "#008559",
      },
      [RegionSetStatusType.Opportunity]: {
        backgroundColor: "#FFE9D9",
        fillColor: "#FF6600",
      },
      [RegionSetStatusType.Unknown]: {
        backgroundColor: "#000000",
        fillColor: "#FFFFFF",
      }
    };

    return countyStatusToColor[filterContext.getCurrentServiceAreaStatus()];
  }, [filterContext.getCurrentServiceAreaStatus()]);

  const buildFilterBadge = (content: JSX.Element | string, idSuffix: string): JSX.Element => {
    return <InfoBadge
      content={content}
      testId={`network-coverage-map__map-details-box-applied-filter-${idSuffix}`}
      icon={null}
    />;
  }

  const renderMembershipChip = (): JSX.Element | null => {
    if (!membershipLabel) {
      return null;
    }

    const shouldTruncate = membershipLabel.length > MAX_MEMBERSHIP_CHIP_LENGTH;
    const badge = <InfoBadge
      testId="network-coverage-map__map-details-box-membership"
      className="network-coverage-map__map-details-box-membership"
      content={shouldTruncate ? truncateStringWithEllipsis(membershipLabel, MAX_MEMBERSHIP_CHIP_LENGTH) : membershipLabel}
      icon={null}
    />;

    return shouldTruncate
      ? <TooltipWrapper
        position="top"
        text={membershipLabel}
        tooltipBodyClassName="network-coverage-map__map-details-box-membership-tooltip"
      >{badge}</TooltipWrapper>
      : badge;
  };

  const appliedProviderSourceFilters = useMemo(() => {
    return mapDataContext.filterAvailableProviderSources(mapDataContext.selectedProviderFilterOptions)
      .sort(mapDataContext.providerSourceFiltersComparer)
      .map(option => {
        const filterName = ConvertProviderSourceFilterOptionsToDisplay[option];
        return buildFilterBadge(
          filterName,
          option
        );
      });
  }, [mapDataContext.selectedProviderFilterOptions, mapDataContext.filterGainsMinPercent, mapDataContext.filterGainsMaxPercent, mapDataContext.filterServesMinPercent, mapDataContext.filterServesMaxPercent, mapDataContext.filterImpactsMinPercent, mapDataContext.filterImpactsMaxPercent]);

  const appliedProviderMetricsFilters = useMemo(() => {
    const appliedFilters = [];
    if (mapDataContext.filterServesMinPercent !== ZERO_PERCENT_FILTER || mapDataContext.filterServesMaxPercent !== HUNDRED_PERCENT_FILTER) {
      appliedFilters.push(buildFilterBadge(
        `Serves %: ${mapDataContext.filterServesMinPercent}% - ${mapDataContext.filterServesMaxPercent}%`,
        "serves"
      ));
    }

    if (mapDataContext.filterGainsMinPercent !== ZERO_PERCENT_FILTER || mapDataContext.filterGainsMaxPercent !== HUNDRED_PERCENT_FILTER) {
      appliedFilters.push(
        buildFilterBadge(
          `Gains %: ${mapDataContext.filterGainsMinPercent}% - ${mapDataContext.filterGainsMaxPercent}%`,
          "gains"
        )
      );
    }

    if (mapDataContext.filterImpactsMinPercent !== ZERO_PERCENT_FILTER || mapDataContext.filterImpactsMaxPercent !== HUNDRED_PERCENT_FILTER) {
      appliedFilters.push(
        buildFilterBadge(
          `Impacts %: ${mapDataContext.filterImpactsMinPercent}% - ${mapDataContext.filterImpactsMaxPercent}%`,
          "impacts"
        )
      );
    }

    return appliedFilters;
  },
    [
      mapDataContext.filterGainsMinPercent,
      mapDataContext.filterGainsMaxPercent,
      mapDataContext.filterServesMinPercent,
      mapDataContext.filterServesMaxPercent,
      mapDataContext.filterImpactsMinPercent,
      mapDataContext.filterImpactsMaxPercent
    ]
  );

  const appliedPciFilters = useMemo(() => {
    const appliedFilters: JSX.Element[] = [];

    if (pciDataEnabled) {
      if (!!mapDataContext.selectedVolumeIndicators.length && !(mapDataContext.selectedVolumeIndicators.length == filterContext.mappedVolumeIndicators.length)) {
        mapDataContext.selectedVolumeIndicators
          .map(value => {
            appliedFilters.push(
              buildFilterBadge(
                `PCI ${mapDataContext.selectedLineOfBusiness}: ${value.name}`,
                "volumeIndicator"
              )
            );
          });
      }

      if (!!mapDataContext.selectedEffectiveness.length) {
        mapDataContext.selectedEffectiveness
          .slice()
          .sort((a, b) => a - b)
          .map(value => {
            appliedFilters.push(
              buildFilterBadge(
                `Effectiveness: ${value ? value : "Unknown"}`,
                "effectiveness"
              )
            );
          });
      }

      if (!!mapDataContext.selectedEfficiency.length) {
        mapDataContext.selectedEfficiency
          .slice()
          .sort((a, b) => a - b)
          .map(value => {
            appliedFilters.push(
              buildFilterBadge(
                `Efficiency: ${value ? value : "Unknown"}`,
                "efficiency"
              )
            );
          });
      }
      return appliedFilters;
    }
    return [];
  }, [
    mapDataContext.selectedVolumeIndicators,
    mapDataContext.selectedLineOfBusiness,
    mapDataContext.selectedEffectiveness,
    mapDataContext.selectedEfficiency
    ]
  )


  const totalFilters = useMemo(() => {
    return appliedProviderSourceFilters.length + appliedProviderMetricsFilters.length + appliedPciFilters.length;
  }, [appliedProviderSourceFilters, appliedProviderMetricsFilters, appliedPciFilters])

  if(!adequacySummarySpecialtyGroup) {
    return (
      <div className="network-coverage-map__map-details-box-container network-coverage-map__map-details-box-container--idle" data-testid="network-coverage-map__map-details-box-container--idle">
        Configure the inputs and select <strong>Create Map</strong> to see network coverage details for a county.
      </div>
    );
  }

  const totalMembers = !isNullOrUndefined(adequacySummarySpecialtyGroup?.accessSummary?.fullMemberCount)
    ? adequacySummarySpecialtyGroup.accessSummary.fullMemberCount.toLocaleString()
    : "---";

  const membersWithAccess = !isNullOrUndefined(adequacySummarySpecialtyGroup?.accessSummary?.fullMemberCountWithAccess)
    ? adequacySummarySpecialtyGroup.accessSummary.fullMemberCountWithAccess.toLocaleString()
    : "---";

  const membersWithAccessPercent = (adequacySummarySpecialtyGroup?.accessSummary
    && adequacySummarySpecialtyGroup.accessSummary.memberCount > 0
    && adequacySummarySpecialtyGroup.accessSummary.memberAccessPercentage.toFixed(1)) || "---";

  const membersWithoutAccess = !isNullOrUndefined(adequacySummarySpecialtyGroup?.accessSummary?.fullMemberCountWithoutAccess)
    ? adequacySummarySpecialtyGroup.accessSummary.fullMemberCountWithoutAccess.toLocaleString()
    : "---";

  const membersWithoutAccessPercent = (adequacySummarySpecialtyGroup?.accessSummary
    && adequacySummarySpecialtyGroup.accessSummary.memberCount > 0
    && (100 - adequacySummarySpecialtyGroup.accessSummary.memberAccessPercentage).toFixed(1)) || "---";

  const servicingProviderCount = !isNullOrUndefined(adequacySummarySpecialtyGroup?.servicingSummary?.distinctServicingProviderCount)
    ? adequacySummarySpecialtyGroup.servicingSummary.distinctServicingProviderCount.toLocaleString()
    : "---";

  const inCountyServicingProviderCount = !isNullOrUndefined(adequacySummarySpecialtyGroup?.servicingSummary?.inRegionProviderCount)
    ? adequacySummarySpecialtyGroup.servicingSummary.inRegionProviderCount.toLocaleString()
    : "---";

  const isTimeOnlyCalculationMethod = !!adequacySummarySpecialtyGroup?.accessSummary
    && adequacySummarySpecialtyGroup.accessSummary.calculationMethod.toString() === (CalculationMethodEnum.Time.toLowerCase());

  const isDistanceOnlyCalculationMethod = !!adequacySummarySpecialtyGroup?.accessSummary
    && adequacySummarySpecialtyGroup.accessSummary.calculationMethod.toString() === (CalculationMethodEnum.Distance.toLowerCase());

  const avgDistance = (!isTimeOnlyCalculationMethod
    && adequacySummarySpecialtyGroup.accessSummary
    && adequacySummarySpecialtyGroup.accessSummary.memberCount > 0
    && adequacySummarySpecialtyGroup.accessSummary.averageCoveringDistance
    && adequacySummarySpecialtyGroup.accessSummary.averageCoveringDistance.toFixed(1)) || "---";

  const maxDistance = (!isTimeOnlyCalculationMethod
    && adequacySummarySpecialtyGroup.accessSummary
    && adequacySummarySpecialtyGroup.accessSummary.memberCount > 0
    && adequacySummarySpecialtyGroup.accessSummary.maximumDistance
    && adequacySummarySpecialtyGroup.accessSummary.maximumDistance.toFixed(1)) || "---";

  const avgTime = (!isDistanceOnlyCalculationMethod
    && adequacySummarySpecialtyGroup.accessSummary
    && adequacySummarySpecialtyGroup.accessSummary.memberCount > 0
    && adequacySummarySpecialtyGroup.accessSummary.averageCoveringTime
    && adequacySummarySpecialtyGroup.accessSummary.averageCoveringTime.toFixed(1)) || "---";

  const maxTime = (!isDistanceOnlyCalculationMethod
    && adequacySummarySpecialtyGroup.accessSummary
    && adequacySummarySpecialtyGroup.accessSummary.memberCount > 0
    && adequacySummarySpecialtyGroup.accessSummary.maximumTime
    && adequacySummarySpecialtyGroup.accessSummary.maximumTime.toFixed(1)) || "---";

  const showAvgDist = runConfiguration.showDistance && project.showAverageDistance;
  const showMaxDist = runConfiguration.showDistance && project.showMaximumDistance;
  const showAvgTime = runConfiguration.showTime && project.showAverageTime;
  const showMaxTime = runConfiguration.showTime && project.showMaximumTime;

  const detailsItemMaxWidthPercent = (1 / (5 + Number(showAvgDist) + Number(showMaxDist) + Number(showAvgTime) + Number(showMaxTime))) * 100;

  return (
    <div className="network-coverage-map__map-details-box-container">
      <div className="network-coverage-map__map-details-county-box">
        <NetworkCoverageMapCountyDetailInfo 
        info={adequacySummarySpecialtyGroup?.footnote} 
        cmsCreditIsApplying={cmsCreditIsApplying} 
        minProvidersFromRatio={adequacySummarySpecialtyGroup?.servicingSummary?.servicingProviderToMemberRatio ? adequacySummarySpecialtyGroup.servicingSummary.minimumServicingProviders : null}
        />
        <div className="network-coverage-map__map-details-box-chip-row">
          <InfoBadge
            testId="network-coverage-map__map-details-box-county-status"
            className="network-coverage-map__map-details-box-county-status"
            content={filterContext.getCurrentServiceAreaStatus() + " County"}
            borderColor={countyStatusColors.fillColor}
            backgroundColor={countyStatusColors.backgroundColor}
            contentColor={QesColors.QesPrimaryGray8}
            iconColor={countyStatusColors.fillColor}
            icon={QesIcons.Circle}
            iconSide="left"
            iconSize="0.5rem"
          />
          {renderMembershipChip()}
        </div>
      </div>
      <div className="network-coverage-map__map-details-filters-container">
        <div className="provider-member-mapping__map-details-container">
          <div className="details">
            <div className="detail-item" style={{maxWidth:`${detailsItemMaxWidthPercent}%`}}>
              <strong>{servicingProviderCount}</strong> Servicing Providers
            </div>
            <div className="detail-item" style={{maxWidth:`${detailsItemMaxWidthPercent}%`}}>
              <strong>{inCountyServicingProviderCount}</strong> In-County Servicing Providers
            </div>
            <div className="detail-item" style={{maxWidth:`${detailsItemMaxWidthPercent}%`}}>
              <strong>{totalMembers}</strong> Total Members
            </div>
            <div className="detail-item" style={{maxWidth:`${detailsItemMaxWidthPercent}%`}}>
              <strong>{membersWithAccess} ({membersWithAccessPercent}%)</strong> Members with Access
            </div>
            <div className="detail-item" style={{maxWidth:`${detailsItemMaxWidthPercent}%`}}>
              <strong>{membersWithoutAccess} ({membersWithoutAccessPercent}%)</strong> Members without Access
            </div>
            {showAvgDist && (
              <div className="detail-item" style={{maxWidth:`${detailsItemMaxWidthPercent}%`}}>
                <strong>{avgDistance} miles</strong> Average Distance
              </div>
            )}
            {showMaxDist && (
              <div className="detail-item" style={{maxWidth:`${detailsItemMaxWidthPercent}%`}}>
                <strong>{maxDistance} miles</strong> Max Distance
              </div>
            )}
            {showAvgTime && (
              <div className="detail-item" style={{maxWidth:`${detailsItemMaxWidthPercent}%`}}>
                <strong>{avgTime} minutes</strong> Average Time
              </div>
            )}
            {showMaxTime && (
              <div className="detail-item" style={{maxWidth:`${detailsItemMaxWidthPercent}%`}}>
                <strong>{maxTime} minutes</strong> Max Time
              </div>
            )}
          </div>
        </div>
          <div className="network-coverage-map__map-details-applied-filters-container">
            <div className="network-coverage-map__applied-filters">
              <h4 className="header-s">
                {totalFilters > 0 ? "Applied Filters:" : "No Filters Applied"}
              </h4>
              {appliedProviderSourceFilters}
              {appliedProviderMetricsFilters}
              {appliedPciFilters}
            </div>
            <p className="ms-1 mb-0"><i>Applied filters update the provider points shown on the map. Member points and map metrics remain unchanged by provider filter selection</i></p>
          </div>
      </div>
    </div>
  );
};

export default MapDetailsBox;